import { Alert, Box, Button, Chip, Skeleton, Stack, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import {
  assignContractToUnmapped,
  countUnmappedLeaves,
  hiddenOnCreate,
  seedCategoryFactors,
  targetFactorOf,
} from '@timelog/pm-shared';
import type { ProjectResponse } from '@timelog/pm-shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useBudgetModel } from '@/analytics';
import { useApiClient, useContracts, useCreateContract, useProject, useUpdateProject } from '@/api';
import {
  ContractConflictDialog,
  ContractPanel,
  ContractSaveStatusChip,
  ContractsPanel,
  ContractsSectionHeader,
  FirstContractDialog,
  FinancialsDrawerHeading,
  ProjectBudgetAside,
  ProjectBudgetAsideEmpty,
} from '@/components/financials';
import { FinancialsProvider, nextContractName } from '@/components/financials/editor';
import { Panels } from '@/components/layouts';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { ProjectHeaderBar } from '@/components/project';
import { EmptyState, SECTION_HEADER_SX } from '@/components/ui';

/**
 * The Financials screen: the contracts rail and the active contract's budget in
 * a resizable main panel, the project-budget summary in the drawer.
 *
 * Realised data is deliberately NOT here. Two actuals panels used to sit below
 * the contract, and neither belonged on a planning-and-budgeting surface: an
 * untouched project got two titled sections that said only "No time
 * registered yet". They went with the TimeLog write-through (ADR 0035);
 * Planning's task insight still reads realised hours through `/summary`.
 *
 * As on Planning, the header sits outside the scroll container and the split
 * persists its width — see the notes in PlanningPage and
 * docs/adr/0013-resizable-panels-for-page-layout.md.
 */
export const FinancialsPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const projectQuery = useProject(projectId);
  const contractsQuery = useContracts(projectId);
  const createContract = useCreateContract(projectId ?? '');
  const updateProject = useUpdateProject(projectId ?? '');
  const api = useApiClient();
  const budgetModel = useBudgetModel();
  const { t } = useTranslation(['pmFinancials', 'pmCommon']);

  /**
   * The first-contract prompt (D9): set right after the project's FIRST
   * contract is created while unmapped tasks exist. Creating the contract
   * mapped nothing (D8); this is where the user decides.
   */
  const [firstContract, setFirstContract] = useState<{
    contractId: string;
    name: string;
    unmappedCount: number;
  } | null>(null);
  const [connecting, setConnecting] = useState(false);

  // The budget model resolves ONCE before Financials first renders (ADR 0027);
  // without a PostHog key `ready` is immediate with the control defaults.
  if (projectQuery.isPending || contractsQuery.isPending || !budgetModel.ready) {
    return (
      <Box sx={{ p: 3 }} data-automation-id="financials-page">
        <Skeleton height={72} sx={{ mb: 2 }} />
        <Skeleton height={320} />
      </Box>
    );
  }

  const project = projectQuery.data;
  const contracts = contractsQuery.data?.contracts;

  if (projectQuery.error || contractsQuery.error || !project || !contracts) {
    return (
      <Box sx={{ p: 3 }} data-automation-id="financials-page">
        <Alert severity="error">
          {projectQuery.error?.message ??
            contractsQuery.error?.message ??
            t('page.ProjectNotFound')}
        </Alert>
      </Box>
    );
  }

  /**
   * "Connect all tasks" (D9): every unmapped leaf gets the contract, and the
   * project prefill follows — one document PUT.
   */
  const connectAll = async (rendered: ProjectResponse, contractId: string) => {
    setConnecting(true);
    try {
      // Re-read: the mapping is written against the current document and
      // version (BR-20), not the one this page happened to render from.
      const current = await api.get<ProjectResponse>(`/projects/${rendered.id}`);
      const { document } = assignContractToUnmapped(current.document, contractId);
      await updateProject.mutateAsync({ document, version: current.version });
      setFirstContract(null);
    } finally {
      setConnecting(false);
    }
  };

  const firstContractDialog =
    firstContract && projectQuery.data ? (
      <FirstContractDialog
        open
        contractName={firstContract.name}
        unmappedCount={firstContract.unmappedCount}
        busy={connecting}
        onMapManually={() => setFirstContract(null)}
        onConnectAll={() => void connectAll(projectQuery.data, firstContract.contractId)}
      />
    ) : null;

  const customerName = project.document.customer?.snapshot.name;

  const header = () => (
    <ProjectHeaderBar
      projectName={project.document.name}
      projectCode={project.document.code}
      /*
        Renaming here is a plain project PUT (BR-20's version predicate), unlike
        Planning's, which goes through the plan editor because that page holds
        the document. Nothing on this page autosaves the *project* document —
        the contract editor saves contracts — so there is no writer to race.
      */
      onRename={(name) =>
        void updateProject.mutateAsync({
          document: { ...project.document, name },
          version: project.version,
        })
      }
    />
  );

  // No contracts yet: the provider needs one, so the empty state lives here.
  //
  // Structured exactly like a Planning section — header row, then the empty
  // state inside a bordered card — rather than centred in the viewport, which
  // left the project title stranded far above it.
  //
  // It runs through `Panels` like the populated page, but with the drawer
  // landing collapsed to its rail: the panel is present (so the page does not
  // simply lack a right-hand side where Planning has one) without opening a
  // panel that has nothing to total yet. `ProjectBudgetAside` still cannot
  // render — it needs `FinancialsProvider`, which needs a contract — so the
  // drawer holds the provider-free `ProjectBudgetAsideEmpty` and its nudge. A
  // collapsed drawer also answers the reason this used to bypass `Panels`
  // altogether: main takes the whole remainder, so there is no grey third.
  if (contracts.length === 0) {
    /*
      Two buttons, two ids — the same split Planning uses between the toolbar
      (`add-task-button`) and its empty state (`empty-plan-start-button`). One
      shared id would resolve to two elements and break every strict locator
      that clicks `create-first-contract`.
    */
    const addFirstContract = async () => {
      const created = await createContract.mutateAsync({
        // The first contract is named after the project.
        name: nextContractName(t, [], project.document.name),
        type: 'fixed',
        // The same seed `FinancialsProvider.createContract` sends (BR-27 /
        // CALC-25): this path used to send none, so the very first contract
        // fell back to CATEGORY_DEFS and ignored the config entirely.
        seed: {
          hiddenCategories: hiddenOnCreate(budgetModel.config),
          targetFactor: targetFactorOf(budgetModel.config),
          categoryFactors: seedCategoryFactors(budgetModel.config),
        },
      });
      // Count against the project as it is NOW, not the cached read this page
      // rendered from: the user may have come straight from Planning, where
      // the tasks in question were just saved.
      const fresh = await api.get<ProjectResponse>(`/projects/${project.id}`);
      const unmappedCount = countUnmappedLeaves(fresh.document);
      if (unmappedCount > 0) {
        setFirstContract({ contractId: created.id, name: created.name, unmappedCount });
      }
    };
    const addContract = (automationId: string, sx?: SxProps<Theme>) => (
      <Button
        variant="contained"
        startIcon={<MaterialSymbol name="add" size={16} />}
        data-automation-id={automationId}
        onClick={() => void addFirstContract()}
        sx={sx}
      >
        {t('actions.AddContract')}
      </Button>
    );
    return (
      <Box
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
        data-automation-id="financials-page"
      >
        {firstContractDialog}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Panels
            // Its own key, not `pm-financials-panels-v2`: the two page states have
            // different panel semantics, and sharing the key would let this
            // state's layout become the contract page's remembered one. Both
            // land collapsed since 2026-10-14, but each remembers its own.
            autoSaveId="pm-financials-empty-panels"
            defaultCollapsed
            collapseDrawerOnEscape
          >
            <Panels.Main>
              {header()}
              {/*
                The section header and its card are ONE child of PanelContent's
                `Stack gap={2}`, exactly as `PlanPanel` is. As siblings they
                picked up that 16px gap on top of the header row's own `mb: 2`,
                which sat the card 16px lower than Planning's.
              */}
              <Box data-automation-id="financials-budget-section">
                <Stack direction="row" sx={SECTION_HEADER_SX}>
                  <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                    {/* h4, matching PlanPanel: the project title above owns the 20px level. */}
                    <Typography variant="h4" component="h2">
                      {t('pmCommon:term.ProjectBudget')}
                    </Typography>
                    <Chip
                      label={t('page.NoContract')}
                      size="small"
                      color="fog"
                      data-automation-id="financials-meta"
                    />
                  </Stack>
                  {/*
                    No header button here: while there is nothing to show, the
                    card below is the one call to action. The toolbar pair
                    belongs to the populated page.
                  */}
                </Stack>

                <Box
                  data-automation-id="financials-empty-card"
                  sx={{
                    bgcolor: 'var(--bg-panel)',
                    borderRadius: 'var(--border-radius-medium)',
                    border: 'var(--border-width-hairline) solid var(--border-subtle)',
                    overflow: 'hidden',
                  }}
                >
                  <EmptyState
                    automationId="financials-empty"
                    icon="euro"
                    title={t('page.EmptyTitle')}
                    body={t('page.EmptyBody')}
                  >
                    {addContract('create-first-contract', { px: 2, py: 1 })}
                  </EmptyState>
                </Box>
              </Box>
            </Panels.Main>
            <Panels.Drawer label={t('page.DrawerLabel')}>
              <ProjectBudgetAsideEmpty
                projectName={project.document.name}
                projectCode={project.document.code}
                customerName={customerName}
              />
            </Panels.Drawer>
          </Panels>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
      data-automation-id="financials-page"
    >
      {/*
        The prompt is set from the EMPTY branch, but by the time the user reads
        it the contracts refetch has landed and this branch is on screen — so it
        must render here too, or it vanishes the moment it appears.
      */}
      {firstContractDialog}
      <FinancialsProvider project={project} contracts={contracts}>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {/*
            Lands collapsed: the summary is opened by hand (designer's ask,
            2026-10-14). The id moved to `-v2` so a layout remembered from when
            it landed open does not override the new default; once opened, it
            is remembered again like any panel.
          */}
          <Panels autoSaveId="pm-financials-panels-v2" defaultCollapsed collapseDrawerOnEscape>
            <Panels.Main
              /*
                As on Planning: the panel does not scroll, so the project header
                stays put and the contract sections scroll inside themselves
                rather than carrying the page off the top.
              */
              panelContentProps={{ hideOverflow: true, hasPaddingBottom: false }}
              footer={<ContractSaveStatusChip />}
            >
              {header()}
              {/*
                ONE child beside the header, not two. `PanelContent` wraps its
                children in a `Stack gap={2}`, so a section header passed
                alongside the panels would collect that 16px on top of its own
                `mb: 2` and sit lower than Planning's equivalent. `PlanPanel` is
                a single child for exactly this reason, and the empty branch
                above already wraps its header and card the same way.
              */}
              <Box
                data-automation-id="financials-budget-section"
                sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
              >
                <ContractsSectionHeader />
                <Stack
                  direction="row"
                  sx={{ gap: 1, alignItems: 'stretch', flex: 1, minHeight: 0, overflow: 'auto' }}
                >
                  {/*
                    The rail shows for any number of contracts: hiding it on a
                    sole contract (2026-09-24) was reverted on 2026-10-01 at
                    the pilot's request.
                  */}
                  <ContractsPanel />
                  <ContractPanel />
                </Stack>
              </Box>
            </Panels.Main>
            <Panels.Drawer label={t('page.DrawerLabel')} heading={<FinancialsDrawerHeading />}>
              <ProjectBudgetAside />
            </Panels.Drawer>
          </Panels>
        </Box>
        <ContractConflictDialog />
      </FinancialsProvider>
    </Box>
  );
};
